// Wire enums for Exams, ingestion and script credits (build doc 01 §2, §6.2,
// §7). Values are the exact strings the backend sends and accepts; each enum
// is a frozen {VALUE: 'VALUE'} object so a typo is undefined, not a new value.

const enumOf = (...values) =>
  Object.freeze(Object.fromEntries(values.map((value) => [value, value])));

// ------------------------------------------------------------- exams (§2.1)
export const ExamStatus = enumOf('DRAFT', 'PROCESSING', 'NEEDS_YOU', 'MARKED');
export const ExamPath = enumOf('DOUBLE', 'MARK_FOR_ME');
export const ExamStep = enumOf(
  'QUESTIONS',
  'MARKING',
  'RESPONSES',
  'YOUR_MARKS',
  'SAMPLES',
  'RESULTS'
);
export const ExamStage = enumOf(
  'SETUP',
  'CONVERTING',
  'CHECK_PAGES',
  'MARKING',
  'REVIEW',
  'DONE'
);
export const ExamNeed = enumOf(
  'CHECK_PAGES',
  'YOUR_MARKS',
  'SAMPLES',
  'REMARKS',
  'FINISH'
);
export const StepState = enumOf('DONE', 'CURRENT', 'AVAILABLE', 'LOCKED');
export const QuestionType = enumOf(
  'SHORT',
  'EXTENDED',
  'CREATIVE',
  'REFLECTION'
);
export const ToolKind = enumOf('GUIDE', 'RUBRIC');
export const ToolSource = enumOf(
  'SUGGESTED',
  'PAPER',
  'LIBRARY',
  'UPLOAD',
  'CUSTOM'
);
export const ToolIssue = enumOf(
  'POINTS_DONT_ADD_UP',
  'TOP_LEVEL_NOT_WEIGHT',
  'LEVEL_RANGE_INVALID',
  'LEVELS_OVERLAP',
  'LEVELS_GAP',
  'MARKS_CHANGED',
  'KIND_MISMATCH',
  'GUIDE_MISSING_ZERO',
  'GUIDE_MISSING_MAX',
  'GUIDE_DUPLICATE_MARK',
  'EMPTY_DESCRIPTOR',
  'NO_TOOL'
);
export const MarkerKind = enumOf('OWNER', 'HUMAN', 'JEDDAI');
export const MarkerId = Object.freeze({ OWNER: 'owner', JEDDAI: 'jeddai' });
export const MarkRound = enumOf('INITIAL', 'REMARK', 'SAMPLE');
export const MarkSource = enumOf(
  'ANNOTATION',
  'SPREADSHEET',
  'TYPED',
  'DRAWER',
  'AI',
  'SAMPLE',
  'REMARK'
);
export const MarkStatus = enumOf('ACTIVE', 'SUPERSEDED');
export const Category = enumOf(
  'ALIGNED',
  'CLOSE',
  'REMARK',
  'UNMATCHED',
  'PENDING'
);
export const RemarkState = enumOf('NONE', 'OPEN', 'RESOLVED', 'AVERAGED');
export const FinalRule = enumOf(
  'HIGHER_MARK',
  'AVERAGE_UP',
  'REMARK',
  'REMARK_KEPT_OUTSIDE',
  'REMARK_TWO_CLOSEST',
  'SKIP_REMARK_AVERAGE',
  'SINGLE_MARKER',
  'CALIBRATED',
  'SAMPLE',
  'UNCALIBRATED',
  'NOT_ATTEMPTED',
  'MANUAL'
);
export const ResponseFlag = enumOf(
  'NO_TEACHER_MARK',
  'NO_JEDDAI_MARK',
  'JEDDAI_FAILED',
  'OUTSIDE_RANGE',
  'MARK_CHANGED',
  'NOT_ATTEMPTED',
  'LOW_CONFIDENCE_READ',
  'START_NOT_FOUND',
  'POINTS_CLAMPED',
  'ROW_MARK_MISMATCH',
  'LOW_CONFIDENCE',
  'NOT_ATTEMPTED_IGNORED'
);
export const PageFlag = enumOf('HARD_TO_READ');
export const ScriptTag = enumOf(
  'REMARKED',
  'EDITED',
  'OUTSIDE_RANGE',
  'MARK_CHANGED',
  'SAMPLE',
  'UNNAMED',
  'TRANSCRIPTION_EDITED',
  'PROVISIONAL'
);
export const AiStatus = enumOf(
  'QUEUED',
  'RUNNING',
  'DONE',
  'FAILED',
  'SKIPPED'
);
export const FeedbackState = enumOf(
  'NONE',
  'QUEUED',
  'GENERATING',
  'READY',
  'EDITED',
  'APPROVED',
  'FAILED'
);
export const ScriptFeedbackStatus = enumOf(
  'NONE',
  'GENERATING',
  'READY_TO_REVIEW',
  'EDITED',
  'NEEDS_REVIEW',
  'APPROVED',
  'FAILED'
);
export const SatisfactionStatus = enumOf(
  'SATISFIED',
  'PARTIALLY_SATISFIED',
  'NOT_SATISFIED'
);
export const CommentAuthor = enumOf('JEDDAI', 'TEACHER');
export const CalibrationMethod = enumOf(
  'LINEAR',
  'OFFSET_ONLY',
  'PIECEWISE_MONOTONE',
  'ISOTONIC',
  'NONE'
);
export const SampleSelectionRule = enumOf('P15_50_85', 'QUARTILES', 'ALL');
export const JobKind = enumOf(
  'CONVERSION',
  'GROUPING',
  'AI_MARKING',
  'TEACHER_MARKS',
  'RANKING',
  'FEEDBACK',
  'INSIGHTS',
  'PAPER_IMPORT',
  'TOOL_IMPORT',
  'DIGEST',
  'SPREADSHEET_IMPORT'
);
export const RunState = enumOf(
  'QUEUED',
  'RUNNING',
  'DONE',
  'FAILED',
  'PARTIAL'
);
export const ImportKind = enumOf(
  'PAPER',
  'RUBRIC',
  'GUIDE',
  'COMMENT_BANK',
  'SUCCESS_CRITERIA'
);
export const AssetPurpose = enumOf(
  'SCRIPTS_STAGED',
  'PAPER',
  'TOOL_SOURCE',
  'EXEMPLAR',
  'MATERIAL',
  'STIMULUS',
  'MARKS_SPREADSHEET',
  'ROSTER',
  'STUDENT_EMAILS'
);
export const NoticeKind = enumOf(
  'SCRIPTS_READY',
  'MARKING_FINISHED',
  'CREDITS_ADDED'
);
export const LinkScope = enumOf('REPORT', 'STUDENT_REPORT');
export const LinkState = enumOf('ACTIVE', 'EXPIRED', 'REVOKED');
export const HistoryAction = enumOf(
  'JEDDAI_MARKED',
  'JEDDAI_REMARKED',
  'MARK_ADDED',
  'MARK_CHANGED',
  'MARK_REMOVED',
  'REMARKED',
  'REMARK_OUTSIDE_KEPT',
  'REMARK_OUTSIDE_TWO_CLOSEST',
  'REMARK_SKIPPED_AVERAGE',
  'FINAL_EDITED',
  'FINAL_RESET',
  'CALIBRATED',
  'RECALIBRATED',
  'SAMPLE_MARKED',
  'FEEDBACK_EDITED',
  'FEEDBACK_APPROVED',
  'ANNOTATION_MARKS_ASSIGNED'
);
// Re-mark outside the earlier marks (409 REMARK_OUTSIDE_EARLIER_MARKS).
export const OutsideDecision = enumOf('KEEP', 'TWO_CLOSEST');
// List filter and report config values (01 §5.1, §5.11).
export const ExamListRange = enumOf(
  'ALL',
  'LAST_30_DAYS',
  'LAST_90_DAYS',
  'THIS_YEAR'
);
export const ReportInclude = enumOf(
  'MARKS',
  'DOUBLE_MARKING',
  'CLASS_INSIGHTS',
  'FEEDBACK_INSIGHTS',
  'QUESTIONS'
);
export const ReportNames = enumOf('SHOW', 'ANONYMOUS');
export const MarksTemplateContains = enumOf('MARKS_ONLY', 'MARKS_AND_GRADES');

// --------------------------------------------------------- ingestion (§2.2)
export const IngestionOwnerKind = enumOf('EXAM', 'BULK_UPLOAD');
export const IngestionMode = enumOf('AUTO', 'MANUAL');
export const IngestionStatus = enumOf(
  'OPEN',
  'EXPLODING',
  'READING',
  'GROUPING',
  'READY',
  'COMMITTING',
  'COMMITTED',
  'CLOSED',
  'DISCARDED',
  'FAILED'
);
export const FileStatus = enumOf(
  'SIGNED',
  'UPLOADED',
  'EXPLODING',
  'EXPLODED',
  'FAILED',
  'REMOVED'
);
export const PageSource = enumOf('PDF', 'IMAGE', 'WORD', 'ZIP_ENTRY', 'TYPED');
export const PageKind = enumOf(
  'RESPONSE',
  'BLANK',
  'COVER',
  'QUESTION_PAPER',
  'SET_ASIDE'
);
export const ReadStatus = enumOf(
  'PENDING',
  'READING',
  'DONE',
  'FAILED',
  'SKIPPED'
);
export const IdentitySource = enumOf('OCR', 'ROSTER', 'MANUAL', 'CONFIRMED');
export const ScriptRailKind = enumOf(
  'UNNAMED',
  'NEW',
  'CHECK_MATCH',
  'MATCHED'
);
export const SuggestionReason = enumOf(
  'ID_MATCH',
  'NAME_MATCH',
  'TEXT_CONTINUES',
  'PAGE_NUMBER',
  'QUESTION_SEQUENCE',
  'HANDWRITING'
);
export const QuestionStartSource = enumOf('AI', 'MANUAL');
export const RosterSource = enumOf('CLASS', 'UPLOAD', 'MANUAL');
export const PageImageSize = enumOf('THUMB', 'PREVIEW');
// Check pages command language (§6.2). There is no DELETE_SCRIPT.
export const CommandType = enumOf(
  'MOVE_PAGE',
  'REORDER_PAGES',
  'NEW_SCRIPT',
  'SPLIT_SCRIPT',
  'SET_IDENTITY',
  'CONFIRM_MATCH',
  'SET_PAGE_KIND',
  'SET_QUESTION_START',
  'CLEAR_QUESTION_START',
  'ACCEPT_SUGGESTIONS',
  'EDIT_PAGE_TEXT',
  'REVERT_PAGE_TEXT',
  'ROTATE_PAGE',
  'SET_MODE',
  'ASSIGN_PAGES_TO_STUDENT'
);

// ------------------------------------------------------ script credits (§2.3)
export const ProductKind = enumOf('PACK', 'VOLUME', 'BATCH');
export const PackId = enumOf('STARTER_100', 'MARKING_PACK_500');
export const OrderKind = enumOf('CARD', 'INVOICE');
export const OrderStatus = enumOf(
  'PENDING',
  'PAID',
  'FAILED',
  'EXPIRED',
  'VOID',
  'REFUNDED',
  'PARTIALLY_REFUNDED'
);
// LotSource / EntryType follow 04 §5 and the backend's domain enums (FAKE lots
// from the local checkout; the free exam's own FREE_* entries); neither is on a
// wire view today (history rows use HistoryKind).
export const LotSource = enumOf('CARD', 'INVOICE', 'FAKE', 'ADMIN');
export const EntryType = enumOf(
  'GRANT',
  'RESERVE',
  'SETTLE',
  'RELEASE',
  'REFUND',
  'EXPIRE',
  'FREE_RESERVE',
  'FREE_SETTLE',
  'FREE_RELEASE',
  'ADMIN'
);
export const FreeExamStatus = enumOf('AVAILABLE', 'RESERVED', 'USED');
export const PayMode = enumOf('PRE', 'POST');
export const CheckoutMode = enumOf('STRIPE', 'FAKE');
export const InvoiceAudience = enumOf('SCHOOL', 'INDIVIDUAL');
export const HistoryKind = enumOf(
  'PURCHASE',
  'INVOICE_SENT',
  'INVOICE_PAID',
  'INVOICE_VOIDED',
  'EXAM_CHARGE',
  'FREE_EXAM',
  'RELEASE',
  'REFUND',
  'EXPIRY',
  'ADMIN'
);

// --------------------------------------------------------- error codes (§7)
export const ExamsErrorCode = enumOf(
  'NOT_FOUND',
  'LINK_INVALID',
  'VALIDATION',
  'VERSION_CONFLICT',
  'EXAM_LOCKED',
  'STEP_NOT_AVAILABLE',
  'SUGGESTED_CRITERIA',
  'OPEN_REMARKS',
  'NAME_REQUIRED',
  'MARKER_LIMIT',
  'OWNER_MARKER',
  'MARK_OVER_MAX',
  'EXEMPLAR_MARK_OVER_MAX',
  'MARK_NOT_INTEGER',
  'MARK_ROW_MISMATCH',
  'NOT_COMMITTED',
  'YOUR_MARKS_WARNINGS',
  'NO_MARKS',
  'HAS_MARKS',
  'NOT_A_REMARK',
  'REMARK_OUTSIDE_EARLIER_MARKS',
  'SAMPLES_INCOMPLETE',
  'ORDER_WARNING',
  'NO_MORE_RESPONSES',
  'DEFAULT_SAMPLE',
  'ANCHOR_INVALID',
  'UNSUPPORTED_FILE_TYPE',
  'FILE_TOO_LARGE',
  'UPLOAD_NOT_FOUND',
  'TOO_MANY_FILES',
  'TOO_MANY_PAGES',
  'TOO_MANY_SCRIPTS',
  'NO_FILES',
  'NO_SCRIPTS',
  'ANNOTATION_ANSWER_REQUIRED',
  'UPLOADS_IN_PROGRESS',
  'UNPAID_CONVERSION_LIMIT',
  'INGESTION_BUSY',
  'INGESTION_NOT_READY',
  'INGESTION_COMMITTED',
  'UNRESOLVED_ITEMS',
  'COMMAND_INVALID',
  'NOTHING_TO_UNDO',
  'CREDITS_REQUIRED',
  'INVALID_QUANTITY',
  'NOTHING_TO_BUY',
  'INVALID_EMAIL',
  'NO_RECIPIENTS',
  'TOO_MANY_RECIPIENTS',
  'DAILY_SEND_LIMIT',
  'ORGANISATION_REQUIRED',
  'INVALID_ABN',
  'INVALID_MOBILE',
  'FEEDBACK_NOT_APPROVED',
  'FEEDBACK_GENERATING',
  'MISSING_EMAILS',
  'IDEMPOTENCY_KEY_REQUIRED',
  'IDEMPOTENCY_KEY_REUSED',
  'RATE_LIMITED',
  'TRY_AGAIN',
  'RESERVATION_FROZEN',
  'PAYMENT_PROVIDER_ERROR',
  'PAYMENT_NOT_CONFIGURED',
  'SIGNATURE_INVALID',
  // Client-side only (service/exams.api.js).
  'UNAUTHENTICATED',
  'NETWORK',
  'ABORTED',
  'UPLOAD_FAILED',
  'EMPTY_RESPONSE'
);
